/**
 * Wire-format schema for `wp/v2/media`.
 *
 * Same snake → camel pattern as the other schemas. We pick the
 * smallest preview size we can find for the picker grid: if WP has
 * image sizes enabled (`media_details.sizes.thumbnail` / `medium`)
 * we take it, otherwise the original `source_url` is the only image
 * available. The dev site currently has sizes off, so the fallback is
 * the common path.
 */
import { z } from 'zod'

import { htmlToPlain } from '@/shared/lib/html'
import { type Media } from '@/entities/media'

const SizeSchema = z
  .object({
    source_url: z.string().url(),
    width: z.number().int().nonnegative().optional(),
    height: z.number().int().nonnegative().optional(),
  })
  .partial()

const MediaWireSchema = z
  .object({
    id: z.number().int().positive(),
    title: z.object({ rendered: z.string() }).optional(),
    alt_text: z.string().optional().default(''),
    mime_type: z.string().optional().default(''),
    media_type: z.string().optional(),
    source_url: z.string().url(),
    media_details: z
      .object({
        width: z.number().int().nonnegative().optional(),
        height: z.number().int().nonnegative().optional(),
        sizes: z.record(z.string(), SizeSchema).optional(),
      })
      .partial()
      .optional(),
  })
  .transform((raw): Media => {
    const sizes = raw.media_details?.sizes ?? {}
    // Prefer thumbnail → medium → full source.
    const preview = sizes.thumbnail?.source_url ?? sizes.medium?.source_url ?? raw.source_url
    return {
      id: raw.id,
      title: htmlToPlain(raw.title?.rendered ?? ''),
      altText: raw.alt_text,
      mimeType: raw.mime_type,
      sourceUrl: raw.source_url,
      thumbnailUrl: preview,
      width: raw.media_details?.width ?? null,
      height: raw.media_details?.height ?? null,
    }
  })

export const mediaWire = MediaWireSchema
export const mediaListWire = z.array(MediaWireSchema)
